import React, { useState } from "react";
import { toast } from "react-hot-toast";
import { Box } from "@mui/material";
import { Pencil, Plus, Trash2 } from "lucide-react";

import { Button } from "components/ui/Button";
import { ConfirmDialog } from "components/ui/ConfirmDialog";
import { DataTable } from "components/ui/DataTable";
import { PageHeader } from "components/ui/PageHeader";

import { TemplatePreview } from "../../../shared/components/TemplatePreview/TemplatePreview";
import layout from "../../../templatesTable.module.css";
import { useCreateSmsTemplate } from "../../hooks/useCreateSmsTemplate";
import { useDeleteSmsTemplate } from "../../hooks/useDeleteSmsTemplate";
import { useSmsTemplatesAll } from "../../hooks/useSmsTemplatesAll";
import { SmsTemplateModal } from "../SmsTemplateModal";

const columns = [
  {
    key: "name",
    header: "Name",
    width: "minmax(150px, 1fr)",
    render: (row) => row.name,
  },
  {
    key: "template",
    header: "Template Text",
    width: "950px",
    render: (row) => <TemplatePreview html={row.template} />,
  },
];

export const SmsSettingsTable = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const { data: templates = [], isLoading } = useSmsTemplatesAll();
  const { mutate: saveTemplate } = useCreateSmsTemplate();
  const { mutate: deleteTemplate } = useDeleteSmsTemplate();

  const handleAddTemplate = () => {
    setSelectedTemplate(null);
    setIsModalOpen(true);
  };

  const handleEditTemplate = (row) => {
    setSelectedTemplate(row);
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setSelectedTemplate(null);
  };

  const handleSubmit = (formValues) => {
    const isEditing = !!selectedTemplate;
    const payload = {
      name: formValues.name,
      template: formValues.template,
      ...(isEditing ? { id: selectedTemplate.id } : {}),
    };

    saveTemplate(payload, {
      onSuccess: () => {
        toast.success(
          isEditing
            ? "Template updated successfully."
            : "Template saved successfully."
        );
        setIsModalOpen(false);
        setSelectedTemplate(null);
      },
      onError: (error) => toast.error(error.message),
    });
  };

  const handleDeleteConfirm = () => {
    if (!deleteTarget) return;
    deleteTemplate(deleteTarget.id, {
      onSuccess: () => toast.success("Template deleted successfully."),
      onError: (error) => toast.error(error.message),
    });
    setDeleteTarget(null);
  };

  const rowActions = [
    {
      label: "Edit",
      icon: Pencil,
      primary: true,
      onClick: (row) => handleEditTemplate(row),
    },
    {
      label: "Delete",
      icon: Trash2,
      primary: true,
      onClick: (row) => setDeleteTarget(row),
    },
  ];

  return (
    <Box className={layout.tableLayout}>
      <PageHeader title="SMS Templates">
        <Button
          variant="text"
          endIcon={Plus}
          data-testid="add-template-button"
          onClick={handleAddTemplate}
        >
          Add SMS Template
        </Button>
      </PageHeader>

      <DataTable
        columns={columns}
        data={templates}
        isLoading={isLoading}
        emptyTitle="No templates found"
        emptyText="There are no SMS templates to display."
        rowsPerPage={20}
        rowActions={rowActions}
      />

      <ConfirmDialog
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDeleteConfirm}
        title="Delete Template"
        message={`Are you sure you want to delete "${deleteTarget?.name}"?`}
        confirmLabel="Delete"
        confirmVariant="primary"
      />

      <SmsTemplateModal
        isOpen={isModalOpen}
        onClose={handleModalClose}
        onSubmit={handleSubmit}
        editTemplate={selectedTemplate}
      />
    </Box>
  );
};
